import {
  Banner,
  BlockStack,
  Button,
  Card,
  FormLayout,
  InlineStack,
  Layout,
  Page,
  SkeletonBodyText,
  Text,
  TextField,
} from "@shopify/polaris";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";

import { getMe, updateCompanyProfile } from "../api/me";
import { QueryErrorBanner } from "../components/QueryErrorBanner";
import {
  genericRegistrationNoError,
  isValidGenericRegistrationNo,
} from "../utils/invoiceNo";
import { registrationConcept } from "../utils/registrationLabel";

import { t } from "../lib/i18n";

/** 「T」+ 数字13桁。空欄は許容（未入力）。 */
function isValidRegistrationNo(value: string): boolean {
  const trimmed = value.trim();
  if (trimmed === "") return true;
  return /^T\d{13}$/.test(trimmed);
}

export function CompanyProfileSettings() {
  const queryClient = useQueryClient();
  const meQuery = useQuery({ queryKey: ["me"], queryFn: getMe });

  const [name, setName] = useState("");
  const [postalCode, setPostalCode] = useState("");
  const [address, setAddress] = useState("");
  const [tel, setTel] = useState("");
  const [registrationNo, setRegistrationNo] = useState("");
  const [savedFlash, setSavedFlash] = useState(false);

  useEffect(() => {
    const profile = meQuery.data?.company_profile;
    if (profile) {
      setName(profile.company_name ?? "");
      setPostalCode(profile.company_postal_code ?? "");
      setAddress(profile.company_address ?? "");
      setTel(profile.company_tel ?? "");
      setRegistrationNo(profile.company_registration_no ?? "");
    }
  }, [meQuery.data]);

  // 登録番号の「国の概念」（Step 3）。JP＝適格請求書（T+13 検証）、GB/DE/FR/AU/CA＝
  // 国の呼び名でラベルのみ（書式は検証しない）、US・カタログ外＝欄ごと出さない。
  const registration = registrationConcept(meQuery.data?.country_code);
  const isJapanStore = registration?.isJapanInvoiceSystem === true;

  const registrationError =
    registration === null
      ? undefined
      : isJapanStore
        ? isValidRegistrationNo(registrationNo)
          ? undefined
          : t("登録番号は「T」に続けて数字13桁で入力してください")
        : isValidGenericRegistrationNo(registrationNo)
          ? undefined
          : genericRegistrationNoError();

  const mutation = useMutation({
    mutationFn: () =>
      updateCompanyProfile({
        company_name: name.trim() || null,
        company_postal_code: postalCode.trim() || null,
        company_address: address.trim() || null,
        company_tel: tel.trim() || null,
        company_registration_no: registrationNo.trim() || null,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["me"] });
      setSavedFlash(true);
      setTimeout(() => setSavedFlash(false), 3000);
    },
  });

  // データ未着（ロード中・取得失敗）は保存不可＝取得失敗中の空フォームで
  // 既存プロフィールを空値上書きさせない。
  const canSave = Boolean(meQuery.data) && !registrationError;
  const handleSave = () => {
    if (canSave) mutation.mutate();
  };

  return (
    <Page
      title={t("発注元情報")}
      primaryAction={{
        content: t("保存"),
        loading: mutation.isPending,
        disabled: !canSave,
        onAction: handleSave,
      }}
    >
      <Layout>
        <Layout.Section>
          {meQuery.isLoading ? (
            <Card>
              <SkeletonBodyText lines={6} />
            </Card>
          ) : meQuery.isError ? (
            <QueryErrorBanner
              title={t("発注元情報の取得に失敗しました")}
              onRetry={() => void meQuery.refetch()}
            />
          ) : (
            <Card>
              <BlockStack gap="400">
                <Text as="p" variant="bodyMd" tone="subdued">
                  {isJapanStore
                    ? t("発注書（PDF・CSV）のヘッダに「発注元」として表示される自社情報です。未設定の場合、発注書に発注元が表示されません。登録番号は自社が適格請求書発行事業者の場合のみ入力してください。")
                    : t("発注書（PDF・CSV）のヘッダに「発注元」として表示される自社情報です。未設定の場合、発注書に発注元が表示されません。")}
                </Text>
                <FormLayout>
                  <TextField
                    label={t("会社名・屋号")}
                    value={name}
                    onChange={setName}
                    placeholder={t("株式会社〇〇商店")}
                    autoComplete="organization"
                  />
                  <FormLayout.Group>
                    <TextField
                      label={t("郵便番号")}
                      value={postalCode}
                      onChange={setPostalCode}
                      placeholder="150-0001"
                      autoComplete="postal-code"
                    />
                    <TextField
                      label={t("電話番号")}
                      type="tel"
                      value={tel}
                      onChange={setTel}
                      placeholder="03-1234-5678"
                      autoComplete="tel"
                    />
                  </FormLayout.Group>
                  <TextField
                    label={t("住所")}
                    value={address}
                    onChange={setAddress}
                    placeholder={t("東京都渋谷区神宮前1-2-3")}
                    autoComplete="street-address"
                  />
                  {/* 登録番号は国の概念があるときだけ。非 JP は書式ヒントを出さない
                      （国ごとに書式が違う＝誤った形式を断言しない）。 */}
                  {isJapanStore ? (
                    <TextField
                      label={t("適格請求書登録番号（任意）")}
                      value={registrationNo}
                      onChange={setRegistrationNo}
                      placeholder="T1234567890123"
                      autoComplete="off"
                      error={registrationError}
                      helpText={t("自社が適格請求書発行事業者の場合のみ。「T」+ 数字13桁。")}
                    />
                  ) : registration ? (
                    <TextField
                      label={registration.label}
                      value={registrationNo}
                      onChange={setRegistrationNo}
                      autoComplete="off"
                      error={registrationError}
                      helpText={t("任意。発注書に表示されます。形式の自動チェックは行いません。")}
                    />
                  ) : null}
                </FormLayout>
                {savedFlash && <Banner tone="success">{t("発注元情報を保存しました")}</Banner>}
                {mutation.isError && (
                  <Banner tone="critical">
                    {t("保存に失敗しました:")} {mutation.error.message}
                  </Banner>
                )}
                <InlineStack align="end">
                  <Button
                    variant="primary"
                    onClick={handleSave}
                    loading={mutation.isPending}
                    disabled={!canSave}
                  >
                    {t("保存")}
                  </Button>
                </InlineStack>
              </BlockStack>
            </Card>
          )}
        </Layout.Section>
      </Layout>
    </Page>
  );
}
